import { useState } from 'react'
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material'

/**
 * Confirmation d'une action destructrice.
 *
 * Le bouton de confirmation reste en `error` et n'est jamais celui qui a le
 * focus par défaut : on ne valide pas une suppression en tapant Entrée par
 * réflexe. L'erreur éventuelle s'affiche DANS la boîte plutôt que derrière,
 * sinon l'utilisateur croit que rien ne s'est passé.
 */
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Supprimer',
  onConfirm,
  onClose,
}: {
  open: boolean
  title: string
  message: string
  confirmLabel?: string
  /** Rejette pour signaler l'échec : la boîte reste ouverte et affiche l'erreur. */
  onConfirm: () => Promise<void>
  onClose: () => void
}): JSX.Element {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function run(): Promise<void> {
    setBusy(true)
    setError(null)
    try {
      await onConfirm()
      onClose()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Suppression impossible')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      aria-labelledby="confirm-title"
    >
      <DialogTitle id="confirm-title">{title}</DialogTitle>
      <DialogContent>
        <DialogContentText variant="body2">{message}</DialogContentText>
        {error && (
          <Alert severity="error" sx={{ mt: 3 }} role="alert">
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 5, py: 4 }}>
        <Button onClick={onClose} disabled={busy} autoFocus>
          Annuler
        </Button>
        <Button color="error" variant="contained" onClick={() => void run()} disabled={busy}>
          {busy ? 'Suppression…' : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
